/**
 * Find the cockpit inside a downloaded aircraft model.
 *
 * The trainer puts the camera at a fixed design eye and the auto-fit parks the
 * model on its wheels. Those two decisions do not have to agree, and on the
 * bohmerang F-14 they did not: the eye landed 5.35 m aft of the nose, which on
 * a two-seat Tomcat is neither cockpit — it is the bulkhead and canopy bow
 * BETWEEN the pilot and the RIO. The camera was inside solid structure, which
 * looks like a rendering fault rather than a placement one.
 *
 * So the cockpits are measured rather than assumed.
 *
 * HOW
 * A cockpit is a CAVITY. Take a thin slab of vertices near the centreline at a
 * given station and bin them by height: the canopy shell above and the fuselage
 * structure below are dense, and the volume the crew occupies is not. Scan that
 * measure along the fuselage and the two-seat layout falls straight out — two
 * open regions with a dense band between them.
 *
 * Pure arithmetic on a flat array, so it can be driven with synthetic geometry
 * in a test rather than by looking at a picture and hoping.
 */

/** Vertical extent of the open volume near the centreline at one station. */
export function cavityAt(samples, zStation, { halfLength = 0.25, halfWidth = 0.45, minGapM = 0.3 } = {}) {
  const ys = []
  for (let i = 0; i < samples.length; i += 3) {
    if (Math.abs(samples[i]) > halfWidth) continue
    if (Math.abs(samples[i + 2] - zStation) > halfLength) continue
    ys.push(samples[i + 1])
  }
  if (ys.length < 40) return { ok: false, count: ys.length }

  ys.sort((a, b) => a - b)

  // The biggest vertical gap between consecutive surfaces is the open volume.
  // Restricted to above the lower fuselage, or the largest gap on a model with
  // a hollow belly is the fuel bay.
  let best = 0
  let floor = 0
  let ceiling = 0
  for (let i = 1; i < ys.length; i++) {
    const gap = ys[i] - ys[i - 1]
    if (gap > best && ys[i - 1] > -0.9) {
      best = gap
      floor = ys[i - 1]
      ceiling = ys[i]
    }
  }
  return {
    ok: best >= minGapM,
    gapM: best,
    floorY: floor,
    coamingY: ceiling,
    topY: ys[ys.length - 1],
    count: ys.length,
  }
}

/**
 * Walk the fuselage and find where the crew sit.
 *
 * @param {number[]} samples flat xyz, model frame, already oriented
 *        nose-along-minus-Z and scaled to metres
 * @param {object} o
 * @param {number} o.noseZ   nose tip, model frame
 * @param {number} o.lengthM
 * @returns {{ok: boolean, seats: Array<{aftOfNoseM: number, fraction: number,
 *            floorY: number, coamingY: number, canopyY: number}>}}
 */
export function findCockpits(samples, { noseZ, lengthM, stepM = 0.3, fromFrac = 0.12, toFrac = 0.5 }) {
  const raw = []
  for (let m = lengthM * fromFrac; m <= lengthM * toFrac; m += stepM) {
    raw.push({ aftOfNoseM: m, ...cavityAt(samples, noseZ + m) })
  }

  // A gap is not a cockpit unless there is a canopy over it.
  //
  // The forward avionics bay is also a hollow near the centreline and it sits
  // directly in front of the pilot, so without this test the front "seat" runs
  // a metre and a half too far forward and the camera ends up in the radar.
  //
  // The reference is the tallest point found ALONG THIS SCAN, not the model's
  // global maximum — that is the fin tips, more than a metre above any canopy,
  // and comparing against it rejects every station on the aeroplane.
  const canopyRef = raw.reduce((t, s) => Math.max(t, s.topY ?? -Infinity), -Infinity)
  const stations = raw.map((s) => ({ ...s, ok: !!s.ok && s.topY >= canopyRef - 0.45 }))

  // Group contiguous open stations into seats.
  const seats = []
  let run = []
  const flush = () => {
    if (run.length < 2) {
      run = []
      return
    }
    const mid = run[Math.floor(run.length / 2)]
    seats.push({
      aftOfNoseM: mid.aftOfNoseM,
      fraction: Math.round((mid.aftOfNoseM / lengthM) * 1000) / 1000,
      floorY: mid.floorY,
      coamingY: mid.coamingY,
      canopyY: Math.max(...run.map((s) => s.topY)),
      lengthM: run[run.length - 1].aftOfNoseM - run[0].aftOfNoseM + stepM,
    })
    run = []
  }
  for (const s of stations) {
    if (s.ok) run.push(s)
    else flush()
  }
  flush()

  return { ok: seats.length > 0, seats, stations }
}

/**
 * Where should the pilot's eye be, given a cockpit?
 *
 * NOT at the top of the open volume. The coaming is what you look OVER, and
 * your eyes are above it — with the canopy a comfortable distance over your
 * head. Anchoring to the canopy is the more reliable of the two, because the
 * canopy is a clean single surface while the coaming measurement picks up the
 * HUD, the panel shroud and whatever else the modeller put on the glareshield.
 *
 * 0.42 m of clearance, not the 0.62 this started with. Sixty-two centimetres
 * is roughly a head's height too generous: it produced a view where the
 * instrument panel filled four fifths of the screen and the only sky was a
 * sliver in the top corners. A fighter canopy sits close over you, and the
 * whole design of the F-14's is that you can see over the nose.
 *
 * @param {number} eyeBelowCanopyM typical eye-to-canopy clearance in a fighter
 */
export function seatEyeY(seat, eyeBelowCanopyM = 0.42) {
  const fromCanopy = seat.canopyY - eyeBelowCanopyM
  // Never below the coaming: you have to be able to see out.
  return Math.max(fromCanopy, seat.coamingY + 0.1)
}
